import React, { forwardRef, HTMLAttributes, ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';

const Container = styled.div`
	grid-row: 1;
	position: relative;
	width: 100%;
	max-height: 100%;
`;

type CarouselItemProps = { children: ReactNode } & HTMLAttributes<HTMLDivElement>;

export type ImperativeCarouselItem = {
	setPos: (pos: number) => void;
	setActive: (active: boolean) => void;
	getBox: () => {
		width: number;
		offset: number;
		min: number;
		max: number;
	};
	getViewPos: () => number;
};

export default forwardRef<ImperativeCarouselItem, CarouselItemProps>(function CarouselItem(
	{ children, ...props },
	ref
) {
	const container = useRef<HTMLDivElement>(null);
	const [isActive, setIsActive] = useState(false);
	const position = useRef(0);

	useImperativeHandle(ref, () => ({
		getBox: () => {
			const itemOffset = container.current?.offsetLeft ?? 0;
			const itemWidth = container.current?.clientWidth ?? 0;

			return {
				offset: itemOffset,
				width: itemWidth,
				min: -itemOffset - itemWidth,
				max: itemWidth + itemOffset,
			};
		},
		setPos: pos => {
			position.current = pos;
			gsap.set(container.current, {
				x: pos,
			});
		},
		setActive: active => {
			setIsActive(active);
		},
		getViewPos: () => {
			const itemOffset = container.current?.offsetLeft ?? 0;
			return position.current + itemOffset;
		},
	}));

	useEffect(() => {
		gsap.to(container.current, {
			opacity: isActive ? 1 : 0.2,
			duration: 0.5,
		});
	}, [isActive, container.current]);

	return (
		<Container ref={container} {...props}>
			{children}
		</Container>
	);
});
